import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { ToastData } from './toasts.store';
import { AlertNotice } from '../components';
import { getAutoClose } from './utils';
import { useToastDynamicStyles } from './Toasts.styles';

interface ToastContainerProps {
    data: ToastData;
    onHide: (id: string) => void;
    autoClose: number | false;
}

export const ToastContainer = forwardRef<HTMLDivElement, ToastContainerProps>((props, ref) => {
    const { data, onHide, autoClose, ...other } = props;

    const { autoClose: _autoClose, id: _id, position = 'bottom-end', message, ...toastProps } = data;

    const autoCloseDuration = getAutoClose(autoClose, data.autoClose);
    const autoCloseTimeout = useRef<number>(-1);

    const cancelAutoCloseTimer = useCallback(() => {
        window.clearTimeout(autoCloseTimeout.current);
    }, []);

    const handleHide = useCallback(() => {
        if (data.id) {
            onHide(data.id);
        }
        cancelAutoCloseTimer();
    }, [cancelAutoCloseTimer, data.id, onHide]);

    const startAutoCloseTimer = useCallback(() => {
        if (typeof autoCloseDuration === 'number') {
            autoCloseTimeout.current = window.setTimeout(handleHide, autoCloseDuration);
        }
    }, [autoCloseDuration, handleHide]);

    useEffect(() => {
        data.onOpen?.(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        startAutoCloseTimer();
        return cancelAutoCloseTimer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoCloseDuration]);

    const toastCssVariables = useToastDynamicStyles(position);

    return (
        <AlertNotice
            ref={ref}
            style={toastCssVariables}
            onDismiss={handleHide}
            onMouseEnter={cancelAutoCloseTimer}
            onMouseLeave={startAutoCloseTimer}
            data-toast={true}
            {...toastProps}
            {...other}
        >
            {message}
        </AlertNotice>
    );
});
